import * as React from 'react'
import { IconSeekers } from 'ui-seeker/core'
import { formatIntegerPrice } from 'app/@utils/price'
import { useTranslation } from 'react-i18next'
import { AppTooltip } from '../Base'
import { translateBoard, translateRate, localizedRoom } from 'app/@utils/catalog'

/**
 * Línea "vs previous price" pensada para ir DEBAJO del precio de la celda.
 *
 * Estructura visual (ej.):  ↑ +5 € (+2,5 %)
 *
 *   - Flecha caret-up / caret-down según signo del diff (lower price → up → success).
 *   - Diff real con signo + moneda formateada, sin decimales (formatIntegerPrice).
 *   - Porcentaje con signo entre paréntesis.
 *   - Tooltip nativo (title) con info clave del previous_item:
 *     Room | Room raw | Meal plan | Cancellation.
 *
 * Props:
 *   previousItem      — item del back: { priceDashboard, priceDashboardNight,
 *                       diff, perc_diff, room_name, raw_room, board_name, rate_name, ... }
 *   currentPrice      — priceDashboard de la celda actual (total)
 *   currentPriceNight — priceDashboardNight de la celda actual (por noche)
 *   currency          — moneda activa (para formatIntegerPrice)
 *   priceByNight      — bool: si true, el diff se calcula sobre el precio/noche
 *
 * Devuelve null si no hay previous, si falta alguno de los precios, o si el diff es 0.
 */
export default function AppComparisonDelta({
    previousItem,
    currentPrice,
    currentPriceNight,
    currency,
    priceByNight = false,
}) {
    const { t } = useTranslation('common')
    if (!previousItem) return null

    const prev = priceByNight ? previousItem.priceDashboardNight : previousItem.priceDashboard
    const cur = priceByNight ? currentPriceNight : currentPrice
    if (!prev || !cur) return null

    const diff = cur - prev
    if (!diff) return null

    const lower = diff < 0
    const sign = diff > 0 ? '+' : '-'
    const absDiff = Math.abs(diff)
    const perc = (diff / prev) * 100
    const absPerc = Math.abs(perc).toFixed(1)

    const tooltipRows = []
    if (previousItem.room_name) tooltipRows.push([t('fields.room'), localizedRoom({ id_room: previousItem.id_room, name: previousItem.room_name })])
    if (previousItem.raw_room) tooltipRows.push([t('events.roomRaw'), previousItem.raw_room])
    if (previousItem.board_name) tooltipRows.push([t('events.mealPlan'), translateBoard(undefined, previousItem.board_name)])
    if (previousItem.rate_name) tooltipRows.push([t('events.cancellation'), translateRate(undefined, previousItem.rate_name)])

    // Fecha del previous: viene como "YYYY-MM-DD" del back. Si no, intenta normalizar
    // un int YYYYMMDD.
    const rawDate = previousItem.date
    let formattedDate = rawDate || ''
    if (typeof rawDate === 'number' || (typeof rawDate === 'string' && /^\d{8}$/.test(rawDate))) {
        const s = String(rawDate)
        formattedDate = `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}`
    }

    const tooltipContent = <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '11px', lineHeight: 1.3, minWidth: '160px' }}>
        <div style={{ fontWeight: 700, fontSize: '11px', paddingBottom: '4px', borderBottom: '1px solid rgba(255,255,255,0.18)' }}>
            {t('vsPreviousChip.previous')}{formattedDate ? ` - ${formattedDate}` : ''}
        </div>
        {tooltipRows.length > 0 && <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
            {tooltipRows.map(([label, value]) => <div key={label}>
                <span style={{ opacity: 0.75 }}>{label}:</span> {value}
            </div>)}
        </div>}
    </div>

    const line = <div
        className={`flex flex-middle flex-center text-${lower ? 'success' : 'error'}`}
        style={{ fontSize: '10px', fontWeight: 600, gap: '3px', lineHeight: 1 }}
    >
        <IconSeekers
            icon={lower ? 'caret-up' : 'caret-down'}
            iconType="fontAwesome"
            iconWeight="fas"
            style={{ fontSize: '0.85rem' }}
        />
        <span>{sign}{formatIntegerPrice(absDiff, { currency })}</span>
        <span>({sign}{absPerc}%)</span>
    </div>

    return <AppTooltip title={tooltipContent}>{line}</AppTooltip>
}
